/* eslint-disable react/no-array-index-key */
import React from 'react';

import Icon from 'molecules/Icon';
import { iconSet } from 'molecules/Icon/types';

import type { IconIds } from 'molecules/Icon/types';

const BR_REGEX = /<br\s*\/?>|\n/g;
const HIGHLIGHT_TOKEN_REGEX = /(\{%.*?%\})/g;
const ICON_TOKEN_SPLIT_REGEX = /(\{icon(?::[\w-]+)?\})/g;
const ICON_TOKEN_MATCH_REGEX = /^\{icon(?::([\w-]+))?\}$/;

// Plain-text version of a heading for SEO-only rendering (sr-only H1s): strips
// every token this component understands. Kept next to the regexes so a new
// token format can't be added here without the cleanup seeing it.
export const headingPlainText = (heading: string): string =>
  heading
    .replace(ICON_TOKEN_SPLIT_REGEX, ' ')
    .replace(/\{%|%\}/g, '')
    .replace(BR_REGEX, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const renderTextSegment = (segment: string, keyBase: string, defaultIcon?: IconIds) => {
  const tokens = segment.split(ICON_TOKEN_SPLIT_REGEX);

  return tokens.map((token, tIdx) => {
    const iconMatch = token.match(ICON_TOKEN_MATCH_REGEX);
    if (iconMatch) {
      const iconName = (iconMatch[1] as IconIds | undefined) ?? defaultIcon;
      if (iconName && iconSet.has(iconName)) {
        return (
          <Icon
            key={`${keyBase}-icon-${tIdx}`}
            icon={iconName}
            className="mx-2 inline-block h-[0.7em] w-auto align-middle"
          />
        );
      }
    }

    return <React.Fragment key={`${keyBase}-text-${tIdx}`}>{token}</React.Fragment>;
  });
};

interface HeadingHighlightsProps {
  heading: string;
  defaultIcon?: IconIds;
}

const HeadingHighlights = ({ heading, defaultIcon }: HeadingHighlightsProps) => {
  const parts = heading.split(HIGHLIGHT_TOKEN_REGEX);

  return (
    <>
      {parts.map((part, i) => {
        const isHighlight = part.startsWith('{%') && part.endsWith('%}');
        const clean = isHighlight ? part.replace(/\{%|%\}/g, '').trim() : part;

        const pieces = clean.split(BR_REGEX);

        const content = (
          <>
            {pieces.map((text, idx) => (
              <React.Fragment key={`${i}-${idx}`}>
                {renderTextSegment(text, `${i}-${idx}`, defaultIcon)}
                {idx < pieces.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </>
        );

        return isHighlight ? (
          <span key={i} className="text-gray-900 dark:text-primary-300">
            {content}
          </span>
        ) : (
          <span key={i}>{content}</span>
        );
      })}
    </>
  );
};

export default HeadingHighlights;
